import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, profileStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ProfileContext = createContext(null);

const firstName = (name) => {
  const n = (name || "").trim().split(/\s+/)[0] || "";
  return n.slice(0, 24) || "Explorateur";
};

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [max, setMax] = useState(1);
  const [plan, setPlan] = useState("free");
  const [activeId, setActiveId] = useState(() => profileStore.get());
  const [loading, setLoading] = useState(true);
  const seedingRef = useRef(false);

  const load = useCallback(async (uName) => {
    setLoading(true);
    try {
      const { data } = await api.get("/profiles");
      let list = data.profiles || [];
      setMax(data.max || 1);
      setPlan(data.plan || "free");
      // Auto-seed a default explorer so the account always has one (inherits legacy Fil history).
      if (list.length === 0 && !seedingRef.current) {
        seedingRef.current = true;
        try {
          const res = await api.post("/profiles", { name: firstName(uName), avatar: "spark", color: "amber" });
          list = [res.data];
        } finally {
          seedingRef.current = false;
        }
      }
      setProfiles(list);
      // Reconcile active id with the fetched list.
      const stored = profileStore.get();
      if (stored && !list.some((p) => p.id === stored)) {
        profileStore.clear();
        setActiveId(null);
      }
    } catch {
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.id) {
      load(user.name);
    } else if (user === false) {
      setProfiles([]);
      setActiveId(null);
      setLoading(false);
    }
  }, [user, load]);

  const selectProfile = useCallback((p) => {
    profileStore.set(p.id);
    setActiveId(p.id);
  }, []);

  const clearActive = useCallback(() => {
    profileStore.clear();
    setActiveId(null);
  }, []);

  const createProfile = useCallback(async (payload) => {
    const { data } = await api.post("/profiles", payload);
    setProfiles((prev) => [...prev, data]);
    return data;
  }, []);

  const updateProfile = useCallback(async (id, payload) => {
    const { data } = await api.put(`/profiles/${id}`, payload);
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, ...data } : p)));
    return data;
  }, []);

  const deleteProfile = useCallback(async (id) => {
    await api.delete(`/profiles/${id}`);
    setProfiles((prev) => prev.filter((p) => p.id !== id));
    if (profileStore.get() === id) clearActive();
  }, [clearActive]);

  const activeProfile = profiles.find((p) => p.id === activeId) || null;

  return (
    <ProfileContext.Provider
      value={{
        profiles, max, plan, activeProfile, loading,
        selectProfile, clearActive, createProfile, updateProfile, deleteProfile,
        reload: () => load(user?.name),
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export const useProfiles = () => useContext(ProfileContext);
